import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { authClient } from '@/lib/auth-client';
import { clearSession } from '@/lib/session';

/**
 * Signs out server-side, then locally.
 *
 * Clearing local state alone would leave the session row alive on the backend,
 * so the token would keep working for anyone who still had it. The server call
 * is best-effort though: if it fails (offline, already-expired token) the local
 * session is still cleared, because the alternative is trapping the user in an
 * app they have asked to leave.
 *
 * The query cache is reset rather than invalidated — invalidation refetches,
 * and refetching with no token just produces a burst of 401s.
 */
export function useSignOut(): () => Promise<void> {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return async () => {
    try {
      await authClient.signOut();
    } catch {
      // Intentionally ignored — see above.
    } finally {
      clearSession();
      queryClient.clear();
      void navigate({ to: '/login' });
    }
  };
}
